import {
  afterNextRender,
  computed,
  effect,
  inject,
  Injectable,
  Injector,
  Signal,
  signal,
  untracked,
} from '@angular/core';
import { TitledContentComponent } from '@components/titled-content';
import { DataService } from '@shared/services';
import { DialogService } from '@shared/services/dialog';
import { Popin } from '@shared/services/elastic-search/models';
import { PRIMARY_CURRENT_LANGUAGE } from '@shared/services/language';
import { LogsService } from '@shared/services/logs';
import { PlatformContextService } from '@shared/services/platform-context';
import { UrlParamsService } from '@shared/services/url-params';
import { secondsUntilMidnight } from '@shared/utils';
import { of, take } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

/**
 * Service responsible for managing popin dialogs based on route context.
 * Initializes and handles effects to dynamically open or close popins based on the current route.
 * Provides client-side-only behavior, registering listeners for language and route changes to fetch active popins.
 *
 * @remarks
 * This service is designed to be idempotent and should only be initialized once per application lifecycle.
 * It leverages Angular Signals and RxJS Observables to handle reactivity and effects.
 * Popins are fetched dynamically and tied to specific route URLs and languages.
 */
@Injectable({ providedIn: 'root' })
export class RoutePopinDispatcherService {
  private readonly injector = inject(Injector);
  private readonly dataService = inject(DataService);
  private readonly dialogService = inject(DialogService);
  private readonly logs = inject(LogsService);
  private readonly isServer = inject(PlatformContextService).isServer;
  private currentUrl!: Signal<string>;

  private readonly popins = signal<Popin[]>([]);

  /**
   * A computed signal that maps URLs to their corresponding IDs.
   *
   * Iterates through the result of `popins()`, extracting `id` and `urls` from each object,
   * and builds a `Map` where each URL is associated with its corresponding ID.
   *
   * @returns A `Map<string, string>` where keys are URLs and values are their respective IDs.
   * @remarks
   * - Skips entries from `popins()` where `urls` is undefined or null.
   * - Assumes `urls` is iterable (e.g., an array or similar structure).
   *
   * @example
   * const mapper = urlToId();
   * console.log(mapper.get('https://example.com')); // Outputs the corresponding ID, if exists.
   * @private
   */
  private readonly urlToId = computed(() => {
    const out = new Map<string, string>();
    for (const { id, urls } of this.popins()) {
      if (!urls) continue;
      for (const url of urls) out.set(url, id);
    }
    return out;
  });

  private initialized = false;
  private openPopinId = signal<string | null>(null);

  /**
   * Initializes the component by setting up state and side effects if not already initialized.
   *
   * @remarks
   * - This method ensures that initialization occurs only once and skips if running in a server environment.
   * - It sets up effects that execute after the next browser render cycle.
   *
   * @return {void} No return value.
   *
   * @example
   * // Ensure the component is properly initialized before further actions.
   * this.initialize();
   */
  initialize(): void {
    if (this.initialized || this.isServer()) return;
    this.initialized = true;
    this.currentUrl = inject(UrlParamsService).currentUrl();
    afterNextRender(() => this.setupEffects(), { injector: this.injector });
  }

  /**
   * Sets up reactive effects to manage popin interactions and data retrieval.
   *
   * - First effect: Monitors the current URL to determine if a popin should be displayed.
   *   Triggers dialog creation and updates popin state accordingly.
   *
   * - Second effect: Fetches and updates the list of active popins in response to language changes.
   *
   * Side effects:
   * - Opens dialogs using `dialogService`.
   * - Updates the state of `openPopinId` and `popins`.
   * - Communicates with `dataService` to persist closure state of popins.
   * - Logs errors to `logs` in case of ElasticSearch failures.
   *
   * @return {void} This method does not return any value, as it relies on reactive side effects to manage state and UI.
   * @remarks The effects use Angular signals and `injector` for context-aware execution.
   */
  private setupEffects(): void {
    effect(
      () => {
        const currentUrl = this.currentUrl();
        if (!currentUrl) return;

        const urlToId = this.urlToId();
        const id = urlToId.get(currentUrl);
        if (!id || this.openPopinId() === id) return;

        this.dataService
          .get$(`popin::${id}::already-closed`)
          .pipe(take(1))
          .subscribe((alreadyClosed) => {
            if (alreadyClosed) return;

            const popin = untracked(() => this.popins().find((p) => p.id === id));
            const ref = this.dialogService.openDialog(TitledContentComponent, 'SMALL', {
              data: { title: popin?.title, content: popin?.text },
            });
            this.openPopinId.set(id);
            ref.closed.pipe(take(1)).subscribe(() => {
              this.openPopinId.set(null);
              this.dataService
                .set(`popin::${id}::already-closed`, true, secondsUntilMidnight())
                .pipe(take(1))
                .subscribe();
            });
          });
      },
      { injector: this.injector }
    );

    effect(
      () => {
        const lang = PRIMARY_CURRENT_LANGUAGE();
        if (!lang) return;
        void import('@shared/services/elastic-search')
          .then(({ ElasticSearchService }) => {
            const elasticSearchService = this.injector.get(ElasticSearchService);
            elasticSearchService
              .getActivePopins()
              .pipe(
                take(1),
                tap((popins) => this.popins.set(popins)),
                catchError((err) => {
                  this.logs.error('RoutePopinDispatcherService', 'getActivePopins failed', err);
                  this.popins.set([]);
                  return of([]);
                })
              )
              .subscribe();
          })
          .catch((err: unknown) =>
            this.logs.error('RoutePopinDispatcherService', 'Failed to load elastic-search chunk', err)
          );
      },
      { injector: this.injector }
    );
  }
}
